import React, { useState, useRef } from 'react';
import { Flex, Box, Text } from 'rebass/styled-components';
import { ProfilePicture } from '@getro/rombo';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { MoreHorizontal, Trash } from 'lucide-react';
import ContentLoader from 'react-content-loader';
import { useTheme } from 'styled-components';
import { Dropdown } from 'components/molecules/dropdown';
import { Card } from '../../../components/atoms/Card';
import { activeNetworkSelector } from '../../../redux/selectors';
import { RemoveHireModal } from '../removeHireModal';

export const HireContactRenderer = ({ data }) => {
  const { colors } = useTheme();
  const activeNetwork = useSelector(activeNetworkSelector);
  const history = useHistory();
  const hire = data?.original;
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const dropdownRef = useRef(null);

  if (hire?.isSkeleton) {
    return (
      <ContentLoader
        speed={2}
        width={400}
        height={48}
        viewBox="0 0 400 48"
        backgroundColor={colors.neutral[50]}
        foregroundColor={colors.neutral[20]}
        style={{ paddingLeft: '16px' }}
      >
        <circle cx="20" cy="24" r="12" />
        <rect x="40" y="16" rx="3" ry="3" width="120" height="12" />
      </ContentLoader>
    );
  }

  if (!hire?.contact) {
    return '';
  }

  const { contact } = hire;

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Navigate to hire detail view with contact ID, preserving search params
    history.push(`/networks/${activeNetwork.slug}/hires/${contact.id}${history.location.search}`);
  };

  const handleRemoveHireClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropdownRef.current) {
      dropdownRef.current.closeDropdown();
    }
    setShowRemoveModal(true);
  };

  return (
    <>
      <Flex
        p="8px"
        pl="16px"
        alignItems="center"
        onClick={handleClick}
        sx={{
          height: '100%',
          gap: '8px',
          cursor: 'pointer',
          textDecoration: 'none',
          '&:hover': {
            bg: 'neutral.20',
          },
        }}
      >
        <Box width="24px" height="24px" flex="0 0 auto">
          <ProfilePicture
            sx={{
              minWidth: '24px',
              minHeight: '24px',
            }}
            imageUrl={contact.profilePicUrl || ''}
            name={contact.fullName || 'Getro'}
          />
        </Box>
        <Text
          fontSize="14px"
          fontWeight="medium"
          color="text.main"
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            flex: 1,
          }}
        >
          {contact.fullName}
        </Text>
        <Box
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          sx={{ flex: '0 0 auto' }}
        >
          <Dropdown
            ref={dropdownRef}
            placement="bottom-start"
            trigger={
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '16px',
                  height: '16px',
                  cursor: 'pointer',
                  color: 'neutral.500',
                  '&:hover': {
                    color: 'neutral.700',
                  },
                }}
              >
                <Box as={MoreHorizontal} width="16px" height="16px" />
              </Box>
            }
            triggerProps={{
              onClick: (e) => {
                e.preventDefault();
                e.stopPropagation();
              },
            }}
          >
            <Card sx={{ cursor: 'pointer', width: '178px', gap: '4px' }} onClick={(event) => event.stopPropagation()}>
              <Flex
                onClick={handleRemoveHireClick}
                data-testid="remove-hire-option"
                alignItems="center"
                sx={{
                  gap: '8px',
                  color: 'text.error',
                  fontSize: '14px',
                  fontWeight: '400',
                  lineHeight: '24px',
                  py: '4px',
                  '&:hover': {
                    color: 'text.error',
                  },
                }}
              >
                <Box as={Trash} size="16px" />
                <Text fontSize="14px" fontWeight="400" lineHeight="24px">
                  Remove hire
                </Text>
              </Flex>
            </Card>
          </Dropdown>
        </Box>
      </Flex>
      {showRemoveModal && <RemoveHireModal hire={hire} onClose={() => setShowRemoveModal(false)} />}
    </>
  );
};

HireContactRenderer.propTypes = {
  data: PropTypes.object,
};

HireContactRenderer.defaultProps = {
  data: {},
};
